import { useCallback, useEffect, useRef, useState } from 'react';

import { AT_BOTTOM_EPSILON_PX, SETTLE_MS } from '../constants/ui';
import {
  DomEventEnum,
  FabFaceEnum,
  IScrollGuidanceState,
  IUseScrollGuidanceOptions,
  IUseScrollGuidanceResult,
} from '../types/shell';

// Drives the scroll-linked visibility of the sticky action bar and the nav FAB/banner from a
// single passive, capture-phase scroll+resize listener (capture-phase so it also works inside
// an iframe/embed scroller). It measures an anchor element's position as a ratio of the
// viewport and derives the bar's show/hide state and a down/up/hidden face for the FAB, using
// hysteresis bands (and clamping for short pages) so nothing flickers at threshold boundaries.
// State is held in a ref and only pushed to React when a field actually changes, and callers
// can call `suppressUntilSettle` around a programmatic scroll so the browser's own smooth
// scroll doesn't drive the machine mid-flight.

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const INITIAL_STATE: IScrollGuidanceState = {
  barVisible: false,
  fabState: FabFaceEnum.Hidden,
  down: false,
  up: false,
};

const ratioFor = (el: HTMLElement, vh: number, scY: number) => {
  const top = el.getBoundingClientRect().top;

  return { r: top / vh, rMax: (top + scY) / vh };
};

export const useScrollGuidance = ({
  anchorRef,
  downEnabled = true,
  upEnabled = true,
  mobileDownThresholds = false,
  downAnchorRef,
  downThresholds,
  upThresholds,
  downHideAt: downHideAtOverride,
  downIgnoresBar = false,
}: IUseScrollGuidanceOptions): IUseScrollGuidanceResult => {
  const [state, setState] = useState<IScrollGuidanceState>(INITIAL_STATE);

  const stateRef = useRef<IScrollGuidanceState>(INITIAL_STATE);
  const downEnabledRef = useRef(downEnabled);
  downEnabledRef.current = downEnabled;
  const upEnabledRef = useRef(upEnabled);
  upEnabledRef.current = upEnabled;
  const mobileDownThresholdsRef = useRef(mobileDownThresholds);
  mobileDownThresholdsRef.current = mobileDownThresholds;
  const downAnchorRefRef = useRef(downAnchorRef);
  downAnchorRefRef.current = downAnchorRef;
  const downThresholdsRef = useRef(downThresholds);
  downThresholdsRef.current = downThresholds;
  const upThresholdsRef = useRef(upThresholds);
  upThresholdsRef.current = upThresholds;
  const downHideAtOverrideRef = useRef(downHideAtOverride);
  downHideAtOverrideRef.current = downHideAtOverride;
  const downIgnoresBarRef = useRef(downIgnoresBar);
  downIgnoresBarRef.current = downIgnoresBar;

  const suppressedRef = useRef(false);
  const settleTimerRef = useRef<number | null>(null);

  const evaluate = useCallback(() => {
    const anchor = anchorRef?.current;

    if (suppressedRef.current) {
      return;
    }

    if (!anchor) {
      const s = stateRef.current;
      if (s.barVisible || s.fabState !== FabFaceEnum.Hidden || s.down || s.up) {
        stateRef.current = INITIAL_STATE;
        setState(INITIAL_STATE);
      }

      return;
    }

    const vh = window.innerHeight;
    const scY = window.scrollY;
    const scrollHeight = document.documentElement.scrollHeight;
    const maxScroll = Math.max(0, scrollHeight - vh);

    const { r, rMax } = ratioFor(anchor, vh, scY);

    const hideAt = Math.min(0.85, rMax - 0.03);
    const barHideAt = Math.max(0.72, hideAt);

    const prevBar = stateRef.current.barVisible;
    const barVisible = prevBar ? !(r >= barHideAt) : r <= 0.7;

    const downEl = downAnchorRefRef.current?.current ?? anchor;
    const downRatios = downEl === anchor ? { r, rMax } : ratioFor(downEl, vh, scY);
    const explicitThresholds = downThresholdsRef.current;
    const defaultDownHideAt = downHideAtOverrideRef.current ?? (mobileDownThresholdsRef.current ? 0.7 : 0.6);
    const downHideAt = explicitThresholds ? explicitThresholds.hideAt : defaultDownHideAt;
    const downShowAt = explicitThresholds
      ? explicitThresholds.showAt
      : Math.max(mobileDownThresholdsRef.current ? 0.73 : 0.62, Math.min(0.85, downRatios.rMax - 0.03));

    const isAtBottom = maxScroll - scY <= AT_BOTTOM_EPSILON_PX;

    const prevDown = stateRef.current.down;
    let down = prevDown ? !(downRatios.r <= downHideAt) : downRatios.r >= downShowAt;
    down = down && downEnabledRef.current && !isAtBottom;

    const prevUp = stateRef.current.up;
    const explicitUpThresholds = upThresholdsRef.current;
    let up: boolean;
    if (explicitUpThresholds) {
      up = prevUp ? !(r >= explicitUpThresholds.hideAt) : r <= explicitUpThresholds.showAt;
    } else {
      const upShowAt = clamp(maxScroll - vh * 0.35, vh * 0.75, vh * 1.5);
      const eligible = maxScroll >= vh * 0.5;
      up = eligible && (prevUp ? !(scY <= upShowAt - vh * 0.3) : scY >= upShowAt);
    }
    up = up && upEnabledRef.current;

    const upShown = up && !(explicitUpThresholds && down);
    const visible = upShown || (down && (downIgnoresBarRef.current || !barVisible));
    const fabState: FabFaceEnum = !visible ? FabFaceEnum.Hidden : upShown ? FabFaceEnum.Up : FabFaceEnum.Down;

    const next: IScrollGuidanceState = { barVisible, fabState, down, up };
    const prev = stateRef.current;

    if (
      prev.barVisible !== next.barVisible ||
      prev.fabState !== next.fabState ||
      prev.down !== next.down ||
      prev.up !== next.up
    ) {
      stateRef.current = next;
      setState(next);
    }
  }, [anchorRef]);

  const clearSettleTimer = useCallback(() => {
    if (settleTimerRef.current !== null) {
      window.clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }, []);

  const armSettleTimer = useCallback(() => {
    clearSettleTimer();
    settleTimerRef.current = window.setTimeout(() => {
      suppressedRef.current = false;
      settleTimerRef.current = null;
      evaluate();
    }, SETTLE_MS);
  }, [clearSettleTimer, evaluate]);

  const suppressUntilSettle = useCallback(() => {
    suppressedRef.current = true;
    armSettleTimer();
  }, [armSettleTimer]);

  const handleScroll = useCallback(() => {
    if (suppressedRef.current) {
      armSettleTimer();

      return;
    }
    evaluate();
  }, [armSettleTimer, evaluate]);

  useEffect(() => {
    document.addEventListener(DomEventEnum.Scroll, handleScroll, { passive: true, capture: true });
    window.addEventListener(DomEventEnum.Resize, handleScroll, { passive: true });

    evaluate();

    return () => {
      document.removeEventListener(DomEventEnum.Scroll, handleScroll, { capture: true });
      window.removeEventListener(DomEventEnum.Resize, handleScroll);
      clearSettleTimer();
    };
  }, [handleScroll, evaluate, clearSettleTimer]);

  // Re-evaluate when the caller's threshold/anchor configuration changes, not just on scroll.
  useEffect(() => {
    evaluate();
  }, [
    downEnabled,
    upEnabled,
    mobileDownThresholds,
    downAnchorRef,
    downThresholds,
    upThresholds,
    downHideAtOverride,
    downIgnoresBar,
    evaluate,
  ]);

  return { ...state, suppressUntilSettle };
};

export { prefersReducedMotion };
